import { AlertTriangle, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { describeError } from "@/components/query-state";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { PruneRequest } from "@/generated/api/model";
import { usePruneOpportunities } from "@/generated/api/opportunities/opportunities";

import { useInvalidateOpportunities } from "./format";

type PruneMode = "days" | "range" | "all";

const QUICK_DAYS = [2, 7, 30] as const;

export function PruneModal({ open, onClose }: { open: boolean; onClose: () => void }) {
    if (!open) return null;
    return <PruneModalDialog onClose={onClose} />;
}

function PruneModalDialog({ onClose }: { onClose: () => void }) {
    const [mode, setMode] = useState<PruneMode>("days");
    const [days, setDays] = useState("2");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [confirmedAll, setConfirmedAll] = useState(false);

    const invalidate = useInvalidateOpportunities();
    const prune = usePruneOpportunities({
        mutation: {
            onSuccess: (data) => {
                invalidate();
                toast.success(
                    data.pruned === 0
                        ? "No closed windows matched the filter"
                        : `Pruned ${data.pruned} window${data.pruned === 1 ? "" : "s"}`,
                );
                onClose();
            },
            onError: (error) => toast.error(describeError(error)),
        },
    });

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [onClose]);

    const buildRequest = (): PruneRequest | null => {
        if (mode === "all") {
            return { all: true };
        }
        if (mode === "days") {
            const count = Number(days.trim());
            if (!Number.isInteger(count) || count <= 0) return null;
            return { older_than_days: count };
        }
        if (mode === "range") {
            if (!fromDate && !toDate) return null;
            const from = fromDate ? Math.floor(new Date(`${fromDate}T00:00:00Z`).getTime() / 1000) : null;
            const to = toDate ? Math.floor(new Date(`${toDate}T23:59:59Z`).getTime() / 1000) : null;
            return { from, to };
        }
        return null;
    };

    const request = buildRequest();
    const canSubmit = !prune.isPending && request !== null && (mode !== "all" || confirmedAll);

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (request && canSubmit) {
            prune.mutate({ data: request });
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div role="dialog" aria-modal="true" aria-labelledby="prune-dialog-title" className="w-full max-w-md">
                <Card className="shadow-lg">
                    <CardHeader className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Trash2 className="size-5 text-destructive" />
                            <h2 id="prune-dialog-title" className="text-base font-semibold">
                                Prune opportunity history
                            </h2>
                        </div>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            aria-label="Close"
                            onClick={onClose}
                            className="size-8 p-0"
                        >
                            <X className="size-4" />
                        </Button>
                    </CardHeader>
                    <CardContent className="space-y-4 pt-4">
                        <p className="text-xs text-muted-foreground">
                            Remove completed opportunity windows and event audit trails from disk. Active open windows
                            are never pruned.
                        </p>

                        <div className="flex gap-1.5" role="group" aria-label="Prune mode">
                            <Button
                                type="button"
                                size="sm"
                                variant={mode === "days" ? "default" : "outline"}
                                aria-pressed={mode === "days"}
                                onClick={() => setMode("days")}
                            >
                                Older than
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                variant={mode === "range" ? "default" : "outline"}
                                aria-pressed={mode === "range"}
                                onClick={() => setMode("range")}
                            >
                                Date range
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                variant={mode === "all" ? "destructive" : "outline"}
                                aria-pressed={mode === "all"}
                                onClick={() => setMode("all")}
                            >
                                Drop all
                            </Button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            {mode === "days" && (
                                <div className="space-y-2">
                                    <div className="flex items-center gap-1.5">
                                        {QUICK_DAYS.map((d) => (
                                            <Button
                                                key={d}
                                                type="button"
                                                size="sm"
                                                variant={days === String(d) ? "secondary" : "ghost"}
                                                onClick={() => setDays(String(d))}
                                            >
                                                {d} days
                                            </Button>
                                        ))}
                                    </div>
                                    <div className="space-y-1">
                                        <Label htmlFor="older-than-days">Days back</Label>
                                        <Input
                                            id="older-than-days"
                                            inputMode="numeric"
                                            value={days}
                                            onChange={(e) => setDays(e.target.value)}
                                            placeholder="e.g. 2, 30"
                                        />
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        Deletes closed windows that ended more than {days || "?"} days ago.
                                    </p>
                                </div>
                            )}

                            {mode === "range" && (
                                <div className="space-y-2">
                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="space-y-1">
                                            <Label htmlFor="from-date">From</Label>
                                            <Input
                                                id="from-date"
                                                type="date"
                                                value={fromDate}
                                                onChange={(e) => setFromDate(e.target.value)}
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <Label htmlFor="to-date">To</Label>
                                            <Input
                                                id="to-date"
                                                type="date"
                                                value={toDate}
                                                onChange={(e) => setToDate(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        Deletes closed windows within the selected date interval.
                                    </p>
                                </div>
                            )}

                            {mode === "all" && (
                                <div className="space-y-3">
                                    <Alert variant="destructive">
                                        <AlertTriangle className="size-4 shrink-0" />
                                        <div className="space-y-1">
                                            <p className="font-medium">Irreversible action</p>
                                            <p className="text-xs">
                                                This will permanently wipe all completed opportunity windows and event
                                                audit trails from the store.
                                            </p>
                                        </div>
                                    </Alert>
                                    <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
                                        <input
                                            type="checkbox"
                                            checked={confirmedAll}
                                            onChange={(e) => setConfirmedAll(e.target.checked)}
                                            className="rounded border"
                                        />
                                        <span>I confirm that I want to drop all opportunity trails</span>
                                    </label>
                                </div>
                            )}

                            <div className="flex items-center justify-end gap-2 pt-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={onClose}
                                    disabled={prune.isPending}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    size="sm"
                                    variant={mode === "all" ? "destructive" : "default"}
                                    disabled={!canSubmit}
                                >
                                    {prune.isPending ? "Pruning..." : mode === "all" ? "Confirm wipe" : "Confirm prune"}
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
